import React, { useMemo } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { formatCelular } from "../../../functions";

const useStyles = makeStyles(theme => ({
  footer: {
    fontSize: "12px"
  },
  info: {
    minHeight: "64px",
    padding: theme.spacing(4),
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-evenly",
    backgroundColor: "#303030"
  },
  section: {
    width: "20%",
    "& h3": {
      marginBottom: "0.75rem",
      color: theme.palette.common.white,
      fontWeight: 500,
      fontSize: "1.15rem"
    }
  },
  content: {
    color: theme.palette.common.white,

    "& h4": {
      marginTop: "1.25rem",
      fontWeight: 500,
      fontSize: "0.95rem",

      "&:first-child": {
        marginTop: 0
      }
    },
    "& pre": {
      fontFamily: "inherit",
      fontSize: "inherit",
      whiteSpace: "pre-line",
      color: "inherit"
    },
    "& ul": {
      marginTop: theme.spacing(4),
      padding: 0,

      "& li": {
        margin: theme.spacing(1, 0),
        display: "flex",
        alignItems: "center",
        listStyle: "none",

        "& i": {
          width: "15px",
          textAlign: "center",
          fontSize: "1.15rem",
          color: theme.palette.secondary.main
        },
        "& span": {
          marginLeft: "1rem",

          "& a": {
            textDecoration: "none",
            color: "inherit",
            transition: "all 0.25s",

            "&:hover": {
              color: theme.palette.secondary.main
            }
          }
        }
      }
    }
  },
  paymentOptions: {
    width: "15%"
  },
  paymentOptionsContent: {
    padding: theme.spacing(2, 0),
    backgroundColor: theme.palette.common.white,
    borderRadius: "3px",

    "& img": {
      height: "75px",
      margin: "0 auto",
      display: "block"
    }
  },
  wireTransfer: {
    textAlign: "center",
    color: theme.palette.text.secondary
  },
  policy: {
    background: "none",
    color: theme.palette.common.white,
    border: "1px solid rgba(255, 255, 255, 0.12)",
    boxShadow: "none",

    "& svg": {
      color: theme.palette.common.white
    }
  },
  poweredBy: {
    minHeight: "64px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#424242",

    "& span": {
      color: "rgba(255, 255, 255, 0.3)"
    },
    "& img": {
      width: "60vw",
      maxWidth: "120px"
    }
  }
}));

const Footer = ({ store }) => {
  const classes = useStyles();

  const { location } = useMemo(() => {
    let location = "";

    if ((((store || {}).body || {}).locations || []).length > 0) {
      location = store.body.locations[0].address.terms.map(term => encodeURI(term.value)).join("+");
    }

    return { location };
  }, [store]);

  return (
    <footer className={classes.footer}>
      <div className={classes.info}>
        <div className={classes.section}>
          <h3>Sobre nosotros</h3>
          <div className={classes.content}>
            {store.body.description && <p>{store.body.description}</p>}
            <ul>
              {location && (
                <li>
                  <i className="fa fa-map-marker"></i>
                  <span>
                    <a
                      href={`https://www.google.com/maps/place/${location}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {store.body.locations[0].address.description}
                    </a>
                  </span>
                </li>
              )}
              {store.body.config && store.body.config.whatsapp && (
                <li>
                  <i className="fa fa-whatsapp"></i>
                  <span>
                    <a
                      href={`tel:+${store.body.config.whatsapp}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {formatCelular(store.body.config.whatsapp)}
                    </a>
                  </span>
                </li>
              )}
              <li>
                <i className="fa fa-envelope"></i>
                <span>
                  <a
                    href={`mailto:${store.body.config.email}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {store.body.config.email}
                  </a>
                </span>
              </li>
            </ul>
          </div>
        </div>

        {store.body.config.deliveryOptions && (
          <div className={classes.section}>
            <h3>Condiciones de entrega</h3>
            <div className={classes.content}>
              {store.body.config.deliveryOptions.deliveryHome && (
                <>
                  <h4>Entrega a domicilio</h4>
                  <p>{store.body.config.deliveryHomeText}</p>
                </>
              )}
              {store.body.config.deliveryOptions.deliveryConvenience && (
                <>
                  <h4>A convenir con el vendedor</h4>
                  <p>{store.body.config.deliveryConvenienceText}</p>
                </>
              )}
              {store.body.config.deliveryOptions.deliveryPickup && (
                <>
                  <h4>Retiro en tienda</h4>
                  <p>{store.body.config.deliveryPickupText}</p>
                </>
              )}
            </div>
          </div>
        )}

        <div className={clsx(classes.section, classes.paymentOptions)}>
          <h3>Medios de pago</h3>
          <div className={clsx(classes.content, classes.paymentOptionsContent)}>
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/logo-webpay-plus.png`}
              alt="WebPay Plus"
              title="WebPay Plus"
            />
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/logo-onepay.png`}
              alt="OnePay"
              title="OnePay"
            />
            {store.body.config.paymentOptions.includes(10) && (
              <div className={classes.wireTransfer}>
                <img
                  src={`${process.env.PUBLIC_URL}/assets/images/transferencia.png`}
                  alt="Transferencia bancaria"
                  title="Transferencia bancaria"
                />
                <span>Transferencia bancaria</span>
              </div>
            )}
          </div>
        </div>

        {store.body.policies && (
          <div className={clsx(classes.section, classes.policies)}>
            <h3>Políticas</h3>
            <div className={classes.content}>
              {store.body.policies && store.body.policies.cambios && (
                <Accordion className={classes.policy}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="cambios-content"
                    id="cambios-header"
                  >
                    <h4>Cambios</h4>
                  </AccordionSummary>
                  <AccordionDetails>
                    <pre>{store.body.policies.cambios}</pre>
                  </AccordionDetails>
                </Accordion>
              )}

              {store.body.policies && store.body.policies.devoluciones && (
                <Accordion className={classes.policy}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="devoluciones-content"
                    id="devoluciones-header"
                  >
                    <h4>Devoluciones</h4>
                  </AccordionSummary>
                  <AccordionDetails>
                    <pre>{store.body.policies.devoluciones}</pre>
                  </AccordionDetails>
                </Accordion>
              )}
            </div>
          </div>
        )}
      </div>
      <div className={classes.poweredBy}>
        <span>Powered By</span>
        <a href="https://www.carritofacil.cl/" target="_blank" rel="noopener noreferrer">
          <img
            src={`${process.env.PUBLIC_URL}/assets/images/contrast-logo.png`}
            alt="Carrito Fácil"
          />
        </a>
      </div>
    </footer>
  );
};

export default Footer;
